import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, Typography} from "@mui/material";
import {downloadPublished, DownloadFormat, listPublishedComponents, PublishedComponent,
        PublishedDocument, readPublishedDocument} from "../util/api";

/**
 * One published SBOM, for the person it was published to.
 *
 * THREE QUESTIONS AND LEAVE: what is in this, is any of it a problem, and is anybody doing
 * something about it. Everything on this page answers one of those and nothing answers a fourth.
 *
 * THE DENOMINATOR TRAVELS WITH THE NUMBER, WITHOUT EXCEPTION. This is the one screen in the whole
 * product read by somebody with no way to check it: they cannot open the composition, cannot run
 * their own scanner against it, and cannot tell a confident zero from an ignorant one. So a
 * vulnerability count never appears here without the count of components that could not be looked
 * up beside it, and when that second number is not zero the page says what it means in a sentence
 * rather than leaving the reader to work it out.
 *
 * THE HEADER AND THE COMPONENTS ARE TWO READS because a composition runs to hundreds of rows and
 * the answer to "is any of it a problem" should not wait for the answer to "what is in it".
 */
const HEADING = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E0E0E0";

function when(value?: string | null): string {
    if (!value) return "not yet";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function Figure({value, label, tone}: {value: React.ReactNode; label: string; tone?: string}) {
    return (
        <Box sx={{minWidth: 110}}>
            <Typography sx={{fontSize: 26, fontWeight: 600, color: tone ?? HEADING, lineHeight: 1.1}}>
                {value}
            </Typography>
            <Typography sx={{fontSize: 12, color: MUTED, mt: 0.5}}>{label}</Typography>
        </Box>
    );
}

export default function Document({summary, onBack}:
                                 {summary: PublishedDocument; onBack: () => void}) {
    const [header, setHeader] = useState<PublishedDocument | null>(null);
    const [components, setComponents] = useState<PublishedComponent[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        readPublishedDocument(summary.USID).then(result => {
            if (result.status === 200) setHeader(result.data);
            // 404 IS THE ANSWER FOR REVOKED, EXPIRED AND NEVER-EXISTED ALIKE, and the page must
            // repeat it rather than improving on it: saying "your access was withdrawn" would
            // confirm the document is real, which is the fact a withdrawal removes.
            else setError(result.data?.Cause || "That document was not found.");
            setLoading(false);
        });
        listPublishedComponents(summary.USID).then(result => {
            if (result.status === 200) {
                setComponents(result.data?.Results ?? []);
                setTotal(result.data?.Count ?? 0);
            }
        });
    }, [summary.USID]);

    const [taking, setTaking] = useState<DownloadFormat | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    // THE BROWSER FOLLOWS THE URL, we do not fetch it. A presigned GET answered into JavaScript
    // would have to be turned back into a file by hand, and the Content-Disposition the service
    // asked S3 for -- which is what names the saved file -- would be thrown away in the process.
    const take = async (format: DownloadFormat) => {
        setTaking(format);
        setDownloadError(null);
        const result = await downloadPublished(summary.USID, format);
        setTaking(null);
        if (result.status === 200 && result.data?.Url) {
            window.location.href = result.data.Url;
            return;
        }
        setDownloadError(result.data?.Cause || "That download could not be prepared.");
    };

    const quality = header?.Quality ?? {};
    const searchable = Number(quality.Searchable ?? 0);
    // THE DENOMINATOR IS "Searchable", NOT "total minus Unmatchable", and getting that wrong is
    // how this page understated a real composition on the day it shipped.
    //
    // The tier grades a component three ways: Searchable (an identifier a vulnerability feed is
    // keyed on), Unverifiable (an identifier, but not one any feed indexes -- pkg:generic/zlib,
    // say), and Unmatchable (no identifier at all). A count of vulnerabilities is a count over
    // the SEARCHABLE ones only; the other two are equally invisible to a lookup.
    //
    // Peak's own composition is 10 components: 3 searchable, 7 unverifiable, 0 unmatchable. Keyed
    // on Unmatchable this page proudly reported "0 cannot be identified" and showed no caveat,
    // about a document where seven tenths of the contents cannot be checked -- on the one screen
    // in the product read by somebody with no way to find that out for themselves.
    const described = Number(header?.ComponentCount ?? quality.Components ?? 0);
    const unsearchable = Math.max(described - searchable, 0);

    return (
        <Box sx={{maxWidth: 1000, mx: "auto", px: 3, py: 6, boxSizing: "border-box"}}>
            <Button onClick={onBack} sx={{fontSize: 13, textTransform: "none", ml: -1}}>
                &larr; Your documents
            </Button>

            <Typography component="h1" sx={{fontSize: 24, fontWeight: 500, color: HEADING, mt: 1}}>
                {summary.Label || "Untitled"}
            </Typography>
            <Typography sx={{fontSize: 13, color: MUTED, mt: 0.5}}>
                {summary.From ? `Published to you by ${summary.From}` : "Published to you"}
            </Typography>

            {error && <Typography sx={{fontSize: 14, color: "#B3261E", mt: 4}}>{error}</Typography>}
            {loading && !error && (
                <Box sx={{display: "flex", py: 6}}><CircularProgress size={22}/></Box>
            )}

            {header && !error && (
                <>
                    <Box sx={{display: "flex", gap: 4, flexWrap: "wrap", mt: 4, p: 3,
                              border: `1px solid ${BORDER}`, borderRadius: 2}}>
                        <Figure value={described} label="Components"/>
                        {/* NEVER THE COUNT ALONE. Searchable is the denominator the vulnerability
                            number is actually over, and it sits next to it rather than below. */}
                        <Figure value={searchable} label="Can be looked up"/>
                        <Figure value={unsearchable} label="Cannot be looked up"
                                tone={unsearchable ? "#B54708" : undefined}/>
                        <Figure value={header.Vulnerabilities ?? 0} label="Known vulnerabilities"/>
                        <Figure value={header.Unassessed ?? 0} label="Not yet assessed"
                                tone={(header.Unassessed ?? 0) ? "#B54708" : undefined}/>
                    </Box>

                    {/* THE CAVEAT IN WORDS, not just a number in a box. A reader who cannot open
                        the composition has no way to discover for themselves that the count above
                        is over a fraction of what they were given. */}
                    {unsearchable > 0 && (
                        <Typography sx={{fontSize: 13, color: "#B54708", mt: 2, lineHeight: 1.6,
                                         backgroundColor: "#FFFAEB", border: "1px solid #FEDF89",
                                         borderRadius: 2, p: 2}}>
                            Of {described} components, {unsearchable} carry no identifier any
                            vulnerability feed is keyed on. The vulnerability count above is a
                            count over the {searchable} that can actually be searched, not over
                            everything in this product.
                        </Typography>
                    )}

                    <Typography sx={{fontSize: 12, color: MUTED, mt: 2}}>
                        {header.Format}{header.SpecVersion ? ` ${header.SpecVersion}` : ""}
                        {" · "}ingested {when(header.IngestedDatetime)}
                        {" · "}
                        {/* NEVER SCANNED IS NOT A DATE. "checked against advisories published up
                            to not yet" is nonsense, and the honest version is also the more
                            alarming one: nobody has looked. */}
                        {header.LastScannedDatetime
                            ? `checked against advisories published up to ${when(header.LastScannedDatetime)}`
                            : "not yet checked against any advisory feed"}
                    </Typography>

                    {/* TAKE IT AWAY. Three formats and no PDF: the page itself is the
                        human-readable rendering, and a filed artefact needs a renderer this tier
                        does not have. Naming the three honestly beats offering a fourth that
                        produces something nobody would file.

                        THE VEX IS THE ONE WORTH READING TWICE. It carries the manufacturer's
                        decision about each known vulnerability, with the justification and the
                        signatories -- which is the thing a regulated customer is actually asking
                        for when they ask for "the SBOM". */}
                    <Box sx={{display: "flex", gap: 1.5, flexWrap: "wrap", mt: 4,
                              alignItems: "center"}}>
                        <Typography sx={{fontSize: 13, color: MUTED}}>Take it away:</Typography>
                        {([["cyclonedx", "CycloneDX"], ["spdx", "SPDX"],
                           ["vex", "VEX decisions"]] as [DownloadFormat, string][])
                            .map(([format, label]) => (
                            <Button key={format} size="small" variant="outlined"
                                    disabled={taking !== null}
                                    onClick={() => take(format)}
                                    sx={{textTransform: "none", fontSize: 13}}>
                                {taking === format ? "Preparing\u2026" : label}
                            </Button>
                        ))}
                    </Box>
                    {downloadError && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", mt: 1}}>
                            {downloadError}
                        </Typography>
                    )}

                    <Typography component="h2"
                                sx={{fontSize: 16, fontWeight: 600, color: HEADING, mt: 5}}>
                        What is in it{total ? ` (${total})` : ""}
                    </Typography>

                    <Box sx={{mt: 2, border: `1px solid ${BORDER}`, borderRadius: 2,
                              overflow: "hidden"}}>
                        {components.map((one, index) => (
                            <Box key={one.USID}
                                 sx={{display: "flex", gap: 2, px: 2.5, py: 1.5, flexWrap: "wrap",
                                      borderTop: index ? `1px solid ${BORDER}` : "none"}}>
                                <Typography sx={{fontSize: 14, color: HEADING, minWidth: 220,
                                                 fontWeight: 500}}>
                                    {one.Name}
                                </Typography>
                                <Typography sx={{fontSize: 14, color: MUTED, minWidth: 100}}>
                                    {one.Version || "—"}
                                </Typography>
                                <Typography sx={{fontSize: 13, color: MUTED, minWidth: 140}}>
                                    {one.Licence || "licence not stated"}
                                </Typography>
                                <Typography sx={{fontSize: 13, color: MUTED}}>
                                    {one.Supplier || ""}
                                </Typography>
                            </Box>
                        ))}
                        {components.length === 0 && (
                            <Typography sx={{fontSize: 13, color: MUTED, p: 2.5}}>
                                Nothing has been ingested against this yet.
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
}
